import _ from 'lodash';
import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box } from 'rebass/styled-components';
import styled from 'styled-components';

import { statesMap, countryMap } from 'shared/core/constants/geography';
import {
  CheckboxOld,
  InfoBox,
  CheckoutAccordion,
  CheckoutAccordionSummary,
  CheckoutAccordionActions,
} from 'shared/components';
import { DefaultInput, DateInput } from 'shared/components/text-input-with-label';
import { DefaultSelect } from 'shared/components/select-with-label';
import MedOnlyCartWarning from 'shared/modals/med-only-cart-warning';
import PhotoIdUpload from 'checkout/components/sections/user/photo-id-upload';

import { hasIsolatedMenus, isMedOnly } from 'shared/core/helpers/dispensaries';

import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import { medicalCard as medicalCardValidator } from 'shared/core/yup/validators/medical-card';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import {
  CheckoutLabelGrouping,
  CheckoutLabel,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';

const getMedCardLocationInfo = ({ location }) =>
  _.includes([countryMap.JM, countryMap.VC], location?.country)
    ? { verbiage: 'I have a medical card', showStateDropdown: false }
    : { verbiage: 'I have a state issued medical card', showStateDropdown: true };

function Medical(_props, ref) {
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [state, setState] = useState('');
  const [photo, setPhoto] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [forcedCheckboxState, setFakeUncheckedState] = useState(true);
  const Checkout = useCheckout();
  const { Cart, UI, User } = useStores();
  const amplitude = useAmplitude();

  const { order, isDelivery, isPickup } = Cart;
  const { dispensary, medicalOrder, isKioskOrder } = order;
  const { removeMedicalCardFieldsAtCheckout } = dispensary;
  // get location context for appropriate verbiage
  const { verbiage: medCardVerbiage, showStateDropdown } = getMedCardLocationInfo(dispensary);

  // medical checkbox should be hidden for plus checkouts
  const disableMedicalCheckbox = UI.isPlus;

  // grab the schema from our validator
  const medicalCardSchema = medicalCardValidator(medicalOrder, isKioskOrder, isDelivery, dispensary);
  const card = {
    number,
    expirationDate,
    state,
    photo,
    dateOfBirth,
  };

  const isFloridaMedical = dispensary?.location?.state === 'FL' && medicalOrder;
  const showAllotmentBirthdate = isFloridaMedical && (dispensary?.storeSettings?.enableMMURAllotmentCheck ?? false);

  const isOpen = open && !removeMedicalCardFieldsAtCheckout;
  const hideExpand = removeMedicalCardFieldsAtCheckout;

  const requirePhoto =
    (_.get(dispensary, 'storeSettings.requireMedCardPhotoForPickup', false) && isPickup) ||
    (_.get(dispensary, 'storeSettings.requireMedCardPhotoForDelivery', false) && isDelivery);

  function resetFields() {
    transaction(() => {
      setNumber(_.get(Cart, 'order.medicalCard.number', ''));
      setExpirationDate(_.get(Cart, 'order.medicalCard.expirationDate', ''));
      setState(_.get(Cart, 'order.medicalCard.state', ''));
      setPhoto(_.get(Cart, 'order.medicalCard.photo', ''));
      setDateOfBirth(_.get(Cart, 'order.medicalCard.dateOfBirth', ''));
    });
  }

  async function isCardValid(cardToValidate = card) {
    try {
      await medicalCardSchema.validate(cardToValidate, { abortEarly: false });
      return { valid: true };
    } catch (err) {
      return { valid: false, err };
    }
  }

  // if a medical card is changed on the order, run snapshot here
  useEffect(() => {
    resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    order.medicalCard.number,
    order.medicalCard.expirationDate,
    order.medicalCard.state,
    order.medicalCard.photo,
    order.medicalCard.dateOfBirth,
  ]);

  useEffect(() => {
    resetFields();
    async function checkCardOnOrder() {
      const cardOnOrder = {
        number: _.get(Cart, 'order.medicalCard.number', ''),
        expirationDate: _.get(Cart, 'order.medicalCard.expirationDate', ''),
        state: _.get(Cart, 'order.medicalCard.state', ''),
        photo: _.get(Cart, 'order.medicalCard.photo', ''),
        dateOfBirth: _.get(Cart, 'order.medicalCard.dateOfBirth', ''),
      };
      const { valid } = await isCardValid(cardOnOrder);
      const dobMissing = showAllotmentBirthdate && !cardOnOrder.dateOfBirth;
      if (!valid || dobMissing) {
        setOpen(true);
      }
    }
    if (medicalOrder) {
      checkCardOnOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSave() {
    amplitude.log(AmplitudeEvents.checkout.clickSave, {
      componentName: `Med ID Info`,
      dispensaryId: dispensary?.id,
      dispensaryName: dispensary?.name,
    });
    const { valid, err } = await isCardValid();
    if (!valid) {
      console.error(err);
      // we're testing the schema directly here, but nested under medicalCard server side.
      Checkout.addErrors(
        _.map(err.inner, (e) => ({ ...e, card: 'medical', params: { path: `medicalCard.${e.path}` } }))
      );
    }

    if (showAllotmentBirthdate && !dateOfBirth) {
      Checkout.addErrors([{ card: 'medical', params: { path: 'medicalCard.dateOfBirth' } }]);
    }

    const dobValid = !showAllotmentBirthdate || !!dateOfBirth;

    transaction(() => {
      Cart.setMedicalCardNumber(number);
      Cart.setMedicalCardExpiration(expirationDate);
      Cart.setMedicalCardState(state);
      Cart.setMedicalCardPhoto(photo);
      if (showAllotmentBirthdate) {
        Cart.setMedicalCardDateOfBirth(dateOfBirth);
      }
      User.setMedicalCard(_.omit(card, 'dateOfBirth'));
    });

    if (valid && dobValid) {
      Checkout.removeError({ match: 'medical' });
      setOpen(false);
    }
  }

  // onSave is called in validateSubmit.js ln 263.
  useImperativeHandle(ref, () => ({ onSave }));

  async function onMenuTypeChange(type) {
    const { hasMedOnlyProducts, hasRecOnlyProducts } = Cart;

    let canSwitch = true;
    if (isMedOnly(dispensary) && type === 'rec') {
      canSwitch = false;
      setFakeUncheckedState(false);
    } else if (isMedOnly(dispensary) && type === 'med') {
      setFakeUncheckedState(true);
    } else if ((hasIsolatedMenus(dispensary) || hasMedOnlyProducts || hasRecOnlyProducts) && Cart.itemCount > 0) {
      canSwitch = await UI.openMenuSwitchWarningModal();
    } else if (!hasIsolatedMenus(dispensary) && hasMedOnlyProducts && type === 'rec') {
      canSwitch = false;
      setModalOpen(true);
    }
    if (canSwitch) {
      Cart.changePricingType(type, dispensary.id);

      const cartIsEmpty = _.isEmpty(Cart.order.cart);
      if (!isOpen) {
        setOpen(type === 'med'); // open if type === 'med', close if type === 'rec'
      } else {
        setOpen(!open);
      }
      if (cartIsEmpty) {
        UI.showMenu(dispensary.cName, true);
      }
    }
  }

  async function onToggle() {
    resetFields();
    if (!open && !medicalOrder) {
      await onMenuTypeChange(medicalOrder ? 'rec' : 'med');
    }
    if (medicalOrder) {
      setOpen(!open);
    }
    const { valid } = await isCardValid();
    if (valid) {
      Checkout.removeError({ match: 'medical' });
    }
  }

  function modalResolve(val) {
    if (val) {
      Cart.changePricingType('rec', dispensary.id);
      UI.showMenu(dispensary.cName, true);
    }
    setModalOpen(false);
  }

  // open if we go from no errors to errors
  useEffect(() => {
    if (Checkout.getError({ match: 'medical' }).error && !open) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Checkout.getError({ match: 'medical' }).error]);

  const getErrorMessage = (field) => {
    let { message } = Checkout.getError({ path: field });
    if (field === 'medicalCard.expirationDate' && !expirationDate?.length) {
      message = 'Please enter a valid expiration date';
    }
    if (field === 'medicalCard.state' && !state?.length) {
      message = 'Please select a state from the menu';
    }
    if (field === 'medicalCard.number' && !number?.length) {
      message = 'Please enter a valid medical card number';
    }
    if (field === 'medicalCard.dateOfBirth' && !dateOfBirth?.length) {
      message = 'Please enter your date of birth';
    }
    return message;
  };

  return (
    <CheckoutLabelGrouping id='medical' data-testid='medical-section'>
      <CheckoutLabel card>
        <CheckoutSectionHeading>Medical</CheckoutSectionHeading>
      </CheckoutLabel>

      <CheckoutBody>
        <CheckoutAccordion
          dataCyName='medical'
          expanded={isOpen}
          onCancel={onToggle}
          onExpand={hideExpand ? null : onToggle}
          onSave={onSave}
          error={Checkout.getError({ match: 'medical' }).error}
        >
          <CheckoutAccordionSummary>
            <CheckboxOld
              name='medicalOrder'
              label={medCardVerbiage}
              aria-label={medCardVerbiage}
              checked={!forcedCheckboxState ? false : medicalOrder}
              data-cy='medical-card-checkbox'
              data-test='medical-card-checkbox'
              data-testid='medical-card-checkbox'
              onChange={(event) => {
                onMenuTypeChange(event.currentTarget.checked ? 'med' : 'rec');
              }}
              width={isOpen ? 'calc(100% - 48px)' : '100%'}
              required={medicalOrder}
              disabled={disableMedicalCheckbox}
            />
          </CheckoutAccordionSummary>
          <CheckoutAccordionActions>
            <Box width='100%'>
              <InfoBox mt='14px' mb='21px'>
                {(() => {
                  if (isDelivery) {
                    return 'Your card will be verified upon delivery, and your order total has been adjusted!';
                  }
                  if (isPickup) {
                    return 'Your card will be verified upon pickup, and your order total has been adjusted!';
                  }
                  return 'Your card will be verified, and your order total has been adjusted!';
                })()}
              </InfoBox>
              <StyledInput
                mb='17px'
                id='medicalCardNumber'
                name='medicalCardNumber'
                label='Medical Card Number'
                aria-label='Medical Card Number input'
                data-cy='medical-card-number-input'
                data-test='medical-card-number-input'
                data-testid='medical-card-number-input'
                value={number}
                onChange={({ target }) => setNumber(target.value)}
                error={Checkout.getError({ path: 'medicalCard.number' }).error}
                errorMessage={getErrorMessage('medicalCard.number')}
                onValueAfterError={() => Checkout.removeError({ path: 'medicalCard.number' })}
                inputProps={{ maxLength: 300 }}
                required
              />
              <StyledDateInput
                mb='17px'
                name='medicalCardExpiration'
                id='medicalCardExpiration'
                label='Medical Card Expiration'
                value={expirationDate}
                data-cy='medical-card-expiration-date-input'
                data-test='medical-card-expiration-date-input'
                data-testid='medical-card-expiration-date-input'
                onChange={({ target }) => setExpirationDate(target.value)}
                error={Checkout.getError({ path: 'medicalCard.expirationDate' }).error}
                errorMessage={getErrorMessage('medicalCard.expirationDate')}
                onValueAfterError={() => Checkout.removeError({ path: 'medicalCard.expirationDate' })}
                required
              />
              {dispensary.location.state !== 'DC' && showStateDropdown && (
                <DefaultSelect
                  width='100%'
                  height='45px'
                  mb='17px'
                  id='medicalState'
                  name='medicalState'
                  label='Medical Card State'
                  data-cy='medical-card-state-selector'
                  data-test='medical-card-state-selector'
                  data-testid='medical-card-state-selector'
                  aria-label='Select Medical Card State'
                  value={state}
                  onChange={({ target }) => setState(target.value)}
                  error={Checkout.getError({ path: 'medicalCard.state' }).error}
                  errorMessage={getErrorMessage('medicalCard.state')}
                  onValueAfterError={() => Checkout.removeError({ path: 'medicalCard.state' })}
                  required
                >
                  {_.map(statesMap, (value, key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </DefaultSelect>
              )}
              {showAllotmentBirthdate && (
                <StyledDateInput
                  mb='17px'
                  name='medicalCardDateOfBirth'
                  id='medicalCardDateOfBirth'
                  label='Date of Birth'
                  aria-label='Date of Birth input'
                  value={dateOfBirth}
                  data-cy='medical-card-date-of-birth-input'
                  data-test='medical-card-date-of-birth-input'
                  data-testid='medical-card-date-of-birth-input'
                  onChange={({ target }) => setDateOfBirth(target.value)}
                  error={Checkout.getError({ path: 'medicalCard.dateOfBirth' }).error}
                  errorMessage={getErrorMessage('medicalCard.dateOfBirth')}
                  onValueAfterError={() => Checkout.removeError({ path: 'medicalCard.dateOfBirth' })}
                  required
                />
              )}
              {requirePhoto && (
                <PhotoIdUpload
                  medical
                  id='medicalPhoto'
                  name='medicalPhoto'
                  aria-label='Upload a photo of you medical card'
                  onPhotoUploaded={(p) => setPhoto(p)}
                  onPhotoReset={() => setPhoto('')}
                  value={photo}
                  error={Checkout.getError({ path: 'medicalCard.photo' }).error}
                  errorMessage={Checkout.getError({ path: 'medicalCard.photo' }).message}
                  onValueAfterError={() => Checkout.removeError({ path: 'medicalCard.photo' })}
                />
              )}
            </Box>
          </CheckoutAccordionActions>
        </CheckoutAccordion>
      </CheckoutBody>
      {modalOpen && <MedOnlyCartWarning resolve={modalResolve} />}
    </CheckoutLabelGrouping>
  );
}

export default observer(forwardRef(Medical));

const StyledInput = styled(DefaultInput)`
  width: 100%;
`;

const StyledDateInput = styled(DateInput)`
  width: 100%;
`;
